import { useMemo, useEffect, useState, type ReactNode } from 'react';
import { useSetAtom } from 'jotai';
import Box from '@mui/material/Box';
import { PageLoader } from '../../shared/PageLoader';
import { EmptyStatePlaceholder } from '@/components/Analysis/shared/EmptyStatePlaceholder';
import { FieldPanel } from '../TemplateForm/styles';
import { IndirectPobFieldsPanel } from './IndirectPobFieldsPanel';
import { IndirectPobTemplateActions } from './IndirectPobTemplateActions';
import {
  indirectPobTemplateFields,
  selectedIndirectPobTemplate,
  selectedIndirectPobTemplateField,
  selectedIndirectPobTemplateId,
} from '../state';
import {
  LogTemplateType,
  useAllLogsTemplatesByTypeQuery,
  useLogsTemplateQuery,
} from '@/__generated__/graphql';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { DerivedFieldsForm } from './KPIDerivedFieldsForm/DerivedFieldsForm';
import { FloatingLine, workpackTabStyles } from './styles';

type IndirectPobTemplateFormProps = {
  structureId: string;
  currentStructureName: string;
  emptyForm: boolean;
  handleEmptyForm: (value: boolean) => void;
};

export const IndirectPobTemplateForm = ({
  structureId,
  currentStructureName,
  emptyForm,
  handleEmptyForm,
}: IndirectPobTemplateFormProps) => {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const setSelectedIndirectPobTemplateId = useSetAtom(selectedIndirectPobTemplateId);
  const setSelectedIndirectPobTemplate = useSetAtom(selectedIndirectPobTemplate);
  const setIndirectPobTemplateFields = useSetAtom(indirectPobTemplateFields);
  const setSelectedIndirectPobTemplateField = useSetAtom(selectedIndirectPobTemplateField);

  const { data: allLogsTemplates, loading: isLogsTemplatesLoading } =
    useAllLogsTemplatesByTypeQuery({
      variables: {
        input: {
          structureId,
          type: LogTemplateType.IndirectPobTemplate,
        },
      },
      skip: !structureId,
      fetchPolicy: 'cache-first',
    });

  const indirectPobTemplate = useMemo(() => {
    return allLogsTemplates?.allLogsTemplates?.at(0) ?? undefined;
  }, [allLogsTemplates?.allLogsTemplates]);

  const hasTemplate = Boolean(indirectPobTemplate?.id);

  const { data: indirectPobTemplateData, loading: isIndirectPobTemplateLoading } =
    useLogsTemplateQuery({
      variables: {
        logsTemplateId: indirectPobTemplate?.id ?? '',
      },
      skip: !indirectPobTemplate?.id,
    });

  useEffect(() => {
    if (indirectPobTemplate?.id) {
      setSelectedIndirectPobTemplateId(indirectPobTemplate.id);
    }
  }, [indirectPobTemplate?.id, setSelectedIndirectPobTemplateId]);

  useEffect(() => {
    if (indirectPobTemplateData?.logsTemplate) {
      setSelectedIndirectPobTemplate(indirectPobTemplateData.logsTemplate);
      setIndirectPobTemplateFields(indirectPobTemplateData.logsTemplate.fields);
      setSelectedIndirectPobTemplateField(indirectPobTemplateData.logsTemplate.fields?.at(0));
    }
  }, [
    indirectPobTemplate,
    indirectPobTemplateData?.logsTemplate,
    setSelectedIndirectPobTemplate,
    setIndirectPobTemplateFields,
    setSelectedIndirectPobTemplateField,
  ]);

  useEffect(() => {
    if (hasTemplate && !isIndirectPobTemplateLoading && emptyForm) {
      handleEmptyForm(false);
    }
  }, [hasTemplate, isIndirectPobTemplateLoading, emptyForm, handleEmptyForm]);

  const handleTabChange = (selectedTabIndex: number) => {
    setSelectedTab(selectedTabIndex);
  };

  const tabs = useMemo(() => {
    return [
      {
        index: 0,
        label: 'Fields',
        component: (
          <IndirectPobFieldsPanel
            structureId={structureId}
            emptyForm={emptyForm}
            handleEmptyForm={handleEmptyForm}
          />
        ),
      },
      {
        index: 1,
        label: 'KPI Derived Fields',
        component: (
          <DerivedFieldsForm
            structureId={structureId}
            hostLogsTemplateId={indirectPobTemplate?.id}
            hostLogsTemplate={indirectPobTemplateData?.logsTemplate}
            hostLogTemplateType={LogTemplateType.IndirectPobTemplate}
          />
        ),
      },
    ];
  }, [
    structureId,
    emptyForm,
    handleEmptyForm,
    indirectPobTemplate?.id,
    indirectPobTemplateData?.logsTemplate,
  ]);

  if (isLogsTemplatesLoading) {
    return <PageLoader />;
  }

  let mainContent: ReactNode;
  if (!hasTemplate) {
    mainContent = (
      <Box sx={{ pt: 2 }}>
        <FieldPanel justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
          <EmptyStatePlaceholder type="template" message="No Indirect POB Template Exists" />
        </FieldPanel>
      </Box>
    );
  } else if (isIndirectPobTemplateLoading) {
    mainContent = <PageLoader />;
  } else {
    mainContent = (
      <Box sx={{ pt: 3.5 }}>
        <BasicTabs
          tabs={tabs}
          selectedTab={selectedTab}
          changeTab={handleTabChange}
          tabsStyles={workpackTabStyles}
          customSeperator={<FloatingLine />}
          renderingStrategy="eager"
        />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 3 }}>
      <IndirectPobTemplateActions
        structureId={structureId}
        currentStructureName={currentStructureName}
        hasTemplate={hasTemplate}
        templateForDownload={
          (indirectPobTemplateData?.logsTemplate ?? indirectPobTemplate) as unknown
        }
        templateIdForSync={indirectPobTemplate?.id}
        linkedStructureIds={indirectPobTemplateData?.logsTemplate?.linkedStructureIds ?? []}
        onAfterAddDefault={() => handleEmptyForm(false)}
      />
      {mainContent}
    </Box>
  );
};
